import { isEmpty } from 'lodash';
import { useCallback, useEffect, useRef } from 'react';

import useDebouncedEffect from '@fhs-legacy/frontend/src/hooks/use-debounce-effect';
import { useCartContext } from '@fhs-legacy/frontend/src/state/cart';
import {
  actions,
  selectors,
  useAppDispatch,
  useAppSelector,
} from '@fhs-legacy/frontend/src/state/global-state';
import { useLoyaltyContext } from '@fhs-legacy/frontend/src/state/loyalty';
import { useLoyaltyUser } from '@fhs-legacy/frontend/src/state/loyalty/hooks/use-loyalty-user';
import { usePaymentContext } from '@fhs-legacy/frontend/src/state/payment';
import { mapPaymentMethodToLoyaltyPaymentMethods } from '@fhs-legacy/frontend/src/utils/payment';

import { useCartValues } from '../../state/cart-store';

export function useLoyaltyIncentivesEvaluation() {
  /*
   * It handles both offers and rewards evaluation, dispatching the results to the Redux store whenever cart changes
   * the 250ms debounce prevents excessive API calls;
   * skipTimeValidation is set to false to ensure time-restricted offers are properly validated
   */
  const { cartEntries } = useCartValues();
  const { calculatedCartTotal } = useCartContext();
  const { loyaltyUserId } = useLoyaltyUser();
  const { checkoutPaymentMethod } = usePaymentContext();
  const dispatch = useAppDispatch();
  const { evaluateLoyaltyUserIncentives, evaluateLoyaltyUserRewards } = useLoyaltyContext();

  const { appliedLoyaltyDiscountOffer, appliedRewards, incentiveSelections, appliedLoyaltyOffers } =
    useAppSelector(state => ({
      appliedLoyaltyDiscountOffer: selectors.loyalty.selectDiscountAppliedCmsOffer(state),
      appliedRewards: selectors.loyalty.selectAppliedLoyaltyRewards(state),
      incentiveSelections: selectors.loyalty.selectSelectedOfferSelections(state),
      appliedLoyaltyOffers: selectors.loyalty.selectAppliedOffers(state),
    }));

  const hasRunInitialEvaluation = useRef(false);

  const runIncentivesEvaluation = useCallback(() => {
    if (!loyaltyUserId) {
      return;
    }
    const paymentMethod = mapPaymentMethodToLoyaltyPaymentMethods(checkoutPaymentMethod);

    if (appliedLoyaltyOffers.length) {
      evaluateLoyaltyUserIncentives({
        loyaltyId: loyaltyUserId,
        appliedLoyaltyOffers,
        cartEntries,
        appliedRewards,
        subtotalAmount: calculatedCartTotal,
        paymentMethod,
        appliedLoyaltyDiscountOffer,
        incentiveSelections,
        skipTimeValidation: false, // Enable time validation to show time-restricted offer messages
      });
    }

    if (isEmpty(appliedRewards)) {
      dispatch(actions.loyalty.resetAppliedLoyaltyRewardsFeedbackMap());
      return;
    }

    evaluateLoyaltyUserRewards({
      loyaltyId: loyaltyUserId,
      appliedLoyaltyRewards: appliedRewards,
      cartEntries,
      subtotalAmount: calculatedCartTotal,
      paymentMethod,
    });
  }, [
    loyaltyUserId,
    appliedLoyaltyOffers,
    cartEntries,
    appliedRewards,
    calculatedCartTotal,
    checkoutPaymentMethod,
    appliedLoyaltyDiscountOffer,
    incentiveSelections,
    evaluateLoyaltyUserIncentives,
    evaluateLoyaltyUserRewards,
    dispatch,
  ]);

  // Run evaluation immediately when conditions are met for the first time
  // This ensures time-restricted offer validation messages appear immediately on cart page load
  useEffect(() => {
    if (
      !hasRunInitialEvaluation.current &&
      loyaltyUserId &&
      appliedLoyaltyOffers.length > 0 &&
      cartEntries.length > 0
    ) {
      hasRunInitialEvaluation.current = true;
      runIncentivesEvaluation();
    }
  }, [loyaltyUserId, appliedLoyaltyOffers.length, cartEntries.length, runIncentivesEvaluation]);

  // Debounced effect only run on on subsequent calls, needs immediate call from useEffect above
  useDebouncedEffect(
    () => {
      if (hasRunInitialEvaluation.current) {
        runIncentivesEvaluation();
      }
    },
    250,
    [
      loyaltyUserId,
      appliedLoyaltyOffers,
      cartEntries,
      appliedRewards,
      calculatedCartTotal,
      checkoutPaymentMethod,
      appliedLoyaltyDiscountOffer,
      incentiveSelections,
      evaluateLoyaltyUserIncentives,
      evaluateLoyaltyUserRewards,
      dispatch,
    ]
  );
}
